// The live DashboardApi: the typed Hono RPC client (../client.ts, built from
// apps/api's AppType) with the operator bearer on every call. Request and
// response types come from AppType; the contract types in ./types.ts are
// asserted equal to them below, so a backend change fails the typecheck here.
import { createApiClient, apiBaseUrl } from "../client";
import { ApiError, type CsvExport, type DashboardApi } from "./api";
import type {
  Connection,
  DataPage,
  Demografi,
  DemografiScope,
  NikCheck,
  SubmissionDetail,
  Issue,
  Ok,
  Overview,
  PairingCode,
  PairingCodeFilter,
  Sender,
  SenderFilter,
  SenderPii,
  SubmissionFilters,
  SubmissionPage,
  SubmissionPii,
} from "./types";

export const UNREACHABLE_MESSAGE = "Server tidak dapat dihubungi";

export interface LiveApiOptions {
  /** Test seam: the fetch the client uses (an in-process app.request). */
  fetch?: typeof fetch;
}

type Query = Record<string, string>;

/** Drops unset keys and stringifies the rest, as query values are strings. */
function query(values: Record<string, string | number | undefined>): Query {
  const out: Query = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") out[key] = String(value);
  }
  return out;
}

interface Reply {
  status: number;
  statusText: string;
  ok: boolean;
  json(): Promise<unknown>;
}

async function errorFrom(res: Reply): Promise<ApiError> {
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const body = (await res.json()) as { error?: unknown; issues?: unknown } | null;
    if (typeof body?.error === "string" && body.error) message = body.error;
    if (Array.isArray(body?.issues)) {
      const issues: Issue[] = body.issues
        .filter((i): i is Issue => typeof i?.path === "string" && typeof i?.message === "string")
        .map((i) => ({ path: i.path, message: i.message }));
      return new ApiError(res.status, message, issues);
    }
  } catch {
    // Not JSON: keep the status text.
  }
  return new ApiError(res.status, message);
}

/** Runs one request; a network failure becomes ApiError(0), a non-2xx an ApiError. */
async function send<R extends Reply>(run: () => Promise<R>): Promise<Extract<R, { status: 200 | 201 }>> {
  let res: R;
  try {
    res = await run();
  } catch {
    throw new ApiError(0, UNREACHABLE_MESSAGE);
  }
  if (!res.ok) throw await errorFrom(res);
  return res as Extract<R, { status: 200 | 201 }>;
}

function filterQuery(s: DemografiScope): Query {
  return query({
    prov: s.prov,
    kab: s.kab,
    kec: s.kec,
    gender: s.gender,
    status: s.status,
    umurMin: s.umurMin,
    umurMax: s.umurMax,
    generasi: s.generasi,
    zona: s.zona,
    q: s.q,
  });
}

function filenameFrom(header: string | null): string {
  const match = header ? /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header) : null;
  return match?.[1] ? decodeURIComponent(match[1]) : "submissions.csv";
}

export function createLiveApi(token: string, baseUrl: string = apiBaseUrl, options: LiveApiOptions = {}): DashboardApi {
  const client = createApiClient(baseUrl, {
    headers: { Authorization: `Bearer ${token}` },
    ...(options.fetch ? { fetch: options.fetch } : {}),
  });
  const dash = client.dashboard;
  const demo = dash.demografi;

  return {
    async validateToken(candidate) {
      // The caller passes the candidate; it may differ from the constructor token.
      const probe = createApiClient(baseUrl, {
        headers: { Authorization: `Bearer ${candidate}` },
        ...(options.fetch ? { fetch: options.fetch } : {}),
      });
      let res: Reply & { status: number };
      try {
        res = await probe.dashboard.session.$get();
      } catch {
        throw new ApiError(0, UNREACHABLE_MESSAGE);
      }
      if (res.status === 200) return true;
      if (res.status === 401) return false;
      throw await errorFrom(res);
    },

    async overview() {
      const res = await send(() => dash.overview.$get());
      return await res.json();
    },

    async listSubmissions(filters: SubmissionFilters) {
      const res = await send(() =>
        dash.submissions.$get({
          query: query({
            q: filters.q,
            phone: filters.phone,
            from: filters.from,
            to: filters.to,
            page: filters.page,
            pageSize: filters.pageSize,
          }),
        }),
      );
      return await res.json();
    },

    async revealSubmission(id) {
      const res = await send(() => dash.submissions[":id"].reveal.$get({ param: { id } }));
      return await res.json();
    },

    async exportCsv(filters: SubmissionFilters): Promise<CsvExport> {
      // fetch, not <a href>: a link cannot carry the Authorization header.
      const res = await send(() =>
        dash["submissions.csv"].$get({
          query: query({ q: filters.q, phone: filters.phone, from: filters.from, to: filters.to }),
        }),
      );
      return { filename: filenameFrom(res.headers.get("content-disposition")), blob: await res.blob() };
    },

    async listCodes(status: PairingCodeFilter) {
      const res = await send(() => dash["pairing-codes"].$get({ query: { status } }));
      return await res.json();
    },

    async createCode(label) {
      const res = await send(() => dash["pairing-codes"].$post({ json: { label } }));
      return await res.json();
    },

    async deleteSubmission(id) {
      const res = await send(() => dash.submissions[":id"].$delete({ param: { id } }));
      return await res.json();
    },

    async deleteCode(id) {
      const res = await send(() => dash["pairing-codes"][":id"].$delete({ param: { id } }));
      return await res.json();
    },

    async listSenders(status: SenderFilter) {
      const res = await send(() => dash.senders.$get({ query: { status } }));
      return await res.json();
    },

    async revealSender(id) {
      const res = await send(() => dash.senders[":id"].reveal.$get({ param: { id } }));
      return await res.json();
    },

    async revokeSender(id) {
      const res = await send(() => dash.senders[":id"].revoke.$post({ param: { id } }));
      return await res.json();
    },

    async restoreSender(id) {
      const res = await send(() => dash.senders[":id"].restore.$post({ param: { id } }));
      return await res.json();
    },

    async connection() {
      const res = await send(() => dash.connection.$get());
      return await res.json();
    },

    async requestPairingCode(phone) {
      const res = await send(() => dash.connection["pairing-code"].$post({ json: { phone } }));
      return await res.json();
    },

    async demografi(scope: DemografiScope) {
      const res = await send(() => demo.$get({ query: filterQuery(scope) }));
      return await res.json();
    },

    async listData(scope: DemografiScope, page: number, pageSize: number) {
      const res = await send(() => demo.data.$get({ query: { ...filterQuery(scope), ...query({ page, pageSize }) } }));
      return await res.json();
    },

    async submissionDetail(id: string) {
      const res = await send(() => demo.kiriman[":id"].$get({ param: { id } }));
      return await res.json();
    },

    async checkNik(nik, jev) {
      const res = await send(() => demo.cek.$post({ json: { nik, jev } }));
      return await res.json();
    },

    async logoutConnection() {
      const res = await send(() => dash.connection.logout.$post());
      return await res.json();
    },
  };
}

// Compile-time assertion that ./types.ts equals what AppType returns, in both
// directions (the method returns above already check AppType -> contract).
type Client = ReturnType<typeof createApiClient>["dashboard"];
type Demo = Client["demografi"];
type Body<R> = R extends { json(): Promise<infer B> } ? B : never;
type Ok200<R> = Extract<R, { status: 200 | 201 }>;
type Equal<A, B> = (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false;
// The nik shapes are the module's own types after Hono's JSON mapping, which
// rewrites some members (literal unions, readonly tuples) without changing what
// can be read; both directions assignable is the check that matters there.
type Mutual<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
const assertContract: [
  Equal<Body<Ok200<Awaited<ReturnType<Client["overview"]["$get"]>>>>, Overview>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["submissions"]["$get"]>>>>, SubmissionPage>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["submissions"][":id"]["reveal"]["$get"]>>>>, SubmissionPii>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["senders"][":id"]["reveal"]["$get"]>>>>, SenderPii>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["pairing-codes"]["$post"]>>>>, PairingCode>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["pairing-codes"]["$get"]>>>>, { items: PairingCode[] }>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["senders"]["$get"]>>>>, { items: Sender[] }>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["connection"]["$get"]>>>>, Connection>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["connection"]["pairing-code"]["$post"]>>>>, Connection>,
  Equal<Body<Ok200<Awaited<ReturnType<Client["connection"]["logout"]["$post"]>>>>, Connection>,
  Mutual<Body<Ok200<Awaited<ReturnType<Demo["$get"]>>>>, Demografi>,
  Mutual<Body<Ok200<Awaited<ReturnType<Demo["cek"]["$post"]>>>>, NikCheck>,
  Mutual<Body<Ok200<Awaited<ReturnType<Demo["data"]["$get"]>>>>, DataPage>,
  Mutual<Body<Ok200<Awaited<ReturnType<Demo["kiriman"][":id"]["$get"]>>>>, SubmissionDetail>,
] = [true, true, true, true, true, true, true, true, true, true, true, true, true, true];
void assertContract;
